import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Camera, Eye, Loader2, Save, Stethoscope } from 'lucide-react';
import { apiClient } from '../utils/api';
import { Act, updateAct, UpdateActData, getActs } from '../services';
import { useToast } from '../components/Toast';
import {
  getPiercingRecord,
  upsertPiercingRecord,
  PiercingRecord,
  UpsertPiercingRecordData,
  PiercingType,
} from '../services/piercingRecordApi';
import {
  getTattooRecord,
  upsertTattooRecord,
  TattooRecord,
  UpsertTattooRecordData,
  TattooStyle,
  ColorType,
  ComplexityLevel,
} from '../services/tattooRecordApi';
import { ActPhotoCaptureModal } from '../components/camera/PhotoCaptureModal';
import { ActPhotoGallery } from '../components/camera/PhotoGallery';
import { listActPhotos, uploadActPhoto, deleteActPhoto } from '../services/actPhotosApi';
import type { ActPhoto } from '../components/camera/types';
import { DateTimeInput } from '../components/DateTimeInput';

type Option = { id: string; name: string };

const toLocalDateTimeInput = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (num: number) => String(num).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

const toIso = (value: string) => {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
};

export function ActEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast, ToastComponent } = useToast();

  const [act, setAct] = useState<Act | null>(null);
  const [formData, setFormData] = useState<UpdateActData>({});
  const [performedAtInput, setPerformedAtInput] = useState('');
  const [plannedStartAtInput, setPlannedStartAtInput] = useState('');
  const [plannedEndAtInput, setPlannedEndAtInput] = useState('');

  const [shops, setShops] = useState<Option[]>([]);
  const [practitioners, setPractitioners] = useState<Option[]>([]);
  const [services, setServices] = useState<Option[]>([]);
  const [clients, setClients] = useState<Option[]>([]);
  const [appointments, setAppointments] = useState<Option[]>([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string[]>([]);

  // Piercing record state
  const [piercingRecord, setPiercingRecord] = useState<PiercingRecord | null>(null);
  const [piercingForm, setPiercingForm] = useState<UpsertPiercingRecordData>({
    piercingType: 'EAR_LOBE',
    placement: { area: '', side: undefined, specific: '' },
    jewelry: { type: '', material: '', gauge: '' },
    technique: { method: '', tools: '' },
    needleGauge: '',
    healingTime: 8,
    aftercareProvided: { products: '', instructions: '' },
    complications: [],
    isHealed: false,
  });
  const [piercingComplicationsText, setPiercingComplicationsText] = useState('');
  const [savingPiercing, setSavingPiercing] = useState(false);
  const [piercingError, setPiercingError] = useState<string | null>(null);

  // Tattoo record state
  const [tattooRecord, setTattooRecord] = useState<TattooRecord | null>(null);
  const [tattooForm, setTattooForm] = useState<UpsertTattooRecordData>({
    style: undefined,
    colorType: undefined,
    complexity: undefined,
    size: { width: undefined, height: undefined, unit: 'cm' },
    placement: { specific: '', side: '' },
    sessionNotes: '',
    isCompleted: false,
  });
  const [savingTattoo, setSavingTattoo] = useState(false);
  const [tattooError, setTattooError] = useState<string | null>(null);

  // Act photos state
  const [actPhotos, setActPhotos] = useState<ActPhoto[]>([]);
  const [photoCaptureOpen, setPhotoCaptureOpen] = useState(false);

  useEffect(() => {
    if (id) {
      loadInitialData(id);
    }
  }, [id]);

  const loadInitialData = async (actId: string) => {
    try {
      setLoading(true);
      setError(null);

      const [shopsRes, practitionersRes, servicesRes, clientsRes, appointmentsRes] = await Promise.all([
        apiClient('/api/admin/shops?limit=1000'),
        apiClient('/api/admin/practitioners?limit=1000'),
        apiClient('/api/admin/services?limit=1000'),
        apiClient('/api/admin/clients?limit=1000'),
        apiClient('/api/admin/appointments?limit=500'),
      ]);

      const [shopsData, practitionersData, servicesData, clientsData, appointmentsData] = await Promise.all([
        shopsRes.json(),
        practitionersRes.json(),
        servicesRes.json(),
        clientsRes.json(),
        appointmentsRes.json(),
      ]);

      if (shopsData.success) {
        setShops(
          (shopsData.data?.shops || []).map((shop: any) => ({
            id: shop.id,
            name: shop.name,
          }))
        );
      }

      if (practitionersData.success) {
        setPractitioners(
          (practitionersData.data?.practitioners || []).map((practitioner: any) => ({
            id: practitioner.id,
            name: `${practitioner.firstName || ''} ${practitioner.lastName || ''}`.trim() || practitioner.email,
          }))
        );
      }

      if (servicesData.success) {
        setServices(
          (servicesData.data?.services || []).map((service: any) => ({
            id: service.id,
            name: service.name,
          }))
        );
      }

      if (clientsData.success) {
        setClients(
          (clientsData.data?.clients || []).map((client: any) => ({
            id: client.id,
            name: `${client.firstName || ''} ${client.lastName || ''}`.trim() || client.email,
          }))
        );
      }

      if (appointmentsData.success) {
        setAppointments(
          (appointmentsData.data?.appointments || []).map((appointment: any) => {
            const clientName = `${appointment.clientFirstName || ''} ${appointment.clientLastName || ''}`.trim()
              || appointment.clientEmail
              || 'Client inconnu';
            const date = appointment.scheduledAt
              ? new Date(appointment.scheduledAt).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
              : '';
            return {
              id: appointment.id,
              name: date ? `${clientName} — ${date}` : clientName,
            };
          })
        );
      }

      const result = await getActs();
      const found = result.acts.find((item) => item.id === actId) || null;
      if (!found) {
        setError('Acte introuvable');
        showToast('Acte introuvable', 'error');
        return;
      }

      setAct(found);
      setFormData({
        appointmentId: found.appointmentId || undefined,
        serviceId: found.serviceId,
        practitionerId: found.practitionerId,
        shopId: found.shopId,
        clientId: found.clientId || undefined,
        sequence: found.sequence || undefined,
        durationMinutes: found.durationMinutes || undefined,
        status: found.status,
        notes: found.notes || '',
      });
      setPlannedStartAtInput(toLocalDateTimeInput(found.plannedStartAt));
      setPlannedEndAtInput(toLocalDateTimeInput(found.plannedEndAt));
      setPerformedAtInput(toLocalDateTimeInput(found.performedAt));

      // Load piercing record if act is of PIERCING category
      if (found.serviceCategory === 'PIERCING') {
        try {
          const record = await getPiercingRecord(actId);
          if (record) {
            setPiercingRecord(record);
            setPiercingForm({
              piercingType: record.piercingType,
              placement: record.placement,
              jewelry: record.jewelry,
              technique: record.technique,
              needleGauge: record.needleGauge,
              healingTime: record.healingTime,
              aftercareProvided: record.aftercareProvided,
              complications: record.complications ?? [],
              isHealed: record.isHealed,
            });
            setPiercingComplicationsText((record.complications ?? []).join(', '));
          }
        } catch {
          // Non-blocking: piercing record may not exist yet
        }
      }

      // Load tattoo record if act is of TATTOO category
      if (found.serviceCategory === 'TATTOO') {
        try {
          const record = await getTattooRecord(actId);
          if (record) {
            setTattooRecord(record);
            setTattooForm({
              style: record.style ?? undefined,
              colorType: record.colorType ?? undefined,
              complexity: record.complexity ?? undefined,
              size: record.size ?? { width: undefined, height: undefined, unit: 'cm' },
              placement: record.placement ?? { specific: '', side: '' },
              sessionNotes: record.sessionNotes ?? '',
              isCompleted: record.isCompleted,
            });
          }
        } catch {
          // Non-blocking: tattoo record may not exist yet
        }
      }

      // Load act photos (for all categories)
      try {
        const photos = await listActPhotos(actId);
        setActPhotos(photos);
      } catch {
        // Non-blocking
      }
    } catch (err: any) {
      console.error('Load act error:', err);
      setError(err.message || 'Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof UpdateActData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!id) return;

    if (!formData.shopId || !formData.practitionerId || !formData.serviceId) {
      setError('Veuillez renseigner la boutique, le praticien et le service');
      showToast('Veuillez remplir les champs obligatoires', 'warning');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setErrorDetails([]);

      await updateAct(id, {
        ...formData,
        appointmentId: formData.appointmentId || undefined,
        clientId: formData.clientId || undefined,
        notes: formData.notes || undefined,
        sequence: formData.sequence || undefined,
        durationMinutes: formData.durationMinutes || undefined,
        plannedStartAt: plannedStartAtInput ? toIso(plannedStartAtInput) : undefined,
        plannedEndAt: plannedEndAtInput ? toIso(plannedEndAtInput) : undefined,
        performedAt: performedAtInput ? toIso(performedAtInput) : undefined,
      });

      showToast('Acte mis à jour', 'success');
      navigate('/planning/acts');
    } catch (err: any) {
      console.error('Update act error:', err);
      const msg = err.message || 'Erreur de connexion au serveur';
      setError(msg);
      setErrorDetails(Array.isArray(err.errors) ? err.errors : []);
      showToast(msg, 'error', 7000);
    } finally {
      setSaving(false);
    }
  };

  const handlePiercingChange = (field: string, value: any) => {
    setPiercingForm((prev) => ({ ...prev, [field]: value }));
  };

  const handlePiercingNested = (section: 'placement' | 'jewelry' | 'technique' | 'aftercareProvided', field: string, value: any) => {
    setPiercingForm((prev) => ({
      ...prev,
      [section]: { ...(prev[section] as any), [field]: value },
    }));
  };

  const handlePiercingSubmit = async () => {
    if (!id) return;
    try {
      setSavingPiercing(true);
      setPiercingError(null);
      const complications = piercingComplicationsText
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const saved = await upsertPiercingRecord(id, { ...piercingForm, complications });
      setPiercingRecord(saved);
      showToast('Fiche piercing enregistrée', 'success');
    } catch (err: any) {
      const msg = err.message || 'Erreur lors de la sauvegarde de la fiche piercing';
      setPiercingError(msg);
      showToast(msg, 'error', 7000);
    } finally {
      setSavingPiercing(false);
    }
  };

  const handleTattooChange = (field: keyof UpsertTattooRecordData, value: any) => {
    setTattooForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleTattooNested = (section: 'size' | 'placement', field: string, value: any) => {
    setTattooForm((prev) => ({
      ...prev,
      [section]: { ...(prev[section] as any), [field]: value },
    }));
  };

  const handleTattooSubmit = async () => {
    if (!id) return;
    try {
      setSavingTattoo(true);
      setTattooError(null);
      const saved = await upsertTattooRecord(id, tattooForm);
      setTattooRecord(saved);
      showToast('Fiche tatouage enregistrée', 'success');
    } catch (err: any) {
      const msg = err.message || 'Erreur lors de la sauvegarde de la fiche tatouage';
      setTattooError(msg);
      showToast(msg, 'error', 7000);
    } finally {
      setSavingTattoo(false);
    }
  };

  const handlePhotoCapture = async (blob: Blob, type: import('../components/camera/types').ActPhotoType) => {
    if (!id) return;
    const photo = await uploadActPhoto(id, blob, type);
    setActPhotos((prev) => [...prev, photo]);
    showToast('Photo enregistrée', 'success');
  };

  const handlePhotoDelete = async (photoId: string) => {
    if (!id) return;
    await deleteActPhoto(id, photoId);
    setActPhotos((prev) => prev.filter((p) => p.id !== photoId));
    showToast('Photo supprimée', 'success');
  };

  const title = useMemo(() => {
    if (!act) return "Modifier l'acte";
    return "Modifier l'acte";
  }, [act]);

  const openGeneratedDocument = (documentId: string, format: 'pdf' | 'docx' = 'pdf') => {
    navigate(`/documents/view/${documentId}?format=${format}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center space-x-2">
          <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          <span className="text-gray-600">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/planning/acts')}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300 shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Retour à la liste</span>
          </button>
          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary">{title}</h1>
          </div>
        </div>
        <button
          type="submit"
          form="act-edit-form"
          disabled={saving}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <Save className="w-4 h-4" />
          <span>{saving ? 'Enregistrement...' : 'Enregistrer'}</span>
        </button>
      </div>

      <ToastComponent />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800 font-medium">{error}</p>
          {errorDetails.length > 0 && (
            <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
              {errorDetails.map((detail, i) => (
                <li key={i}>{detail}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <form id="act-edit-form" onSubmit={handleSubmit} className="space-y-6">
        <div className="card">
          <h2 className="text-xl font-semibold text-textPrimary mb-6">Informations principales</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Boutique *</label>
              <select
                value={formData.shopId || ''}
                onChange={(e) => handleInputChange('shopId', e.target.value)}
                className="input-field"
                required
              >
                <option value="">Sélectionner une boutique</option>
                {shops.map((shop) => (
                  <option key={shop.id} value={shop.id}>
                    {shop.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Praticien *</label>
              <select
                value={formData.practitionerId || ''}
                onChange={(e) => handleInputChange('practitionerId', e.target.value)}
                className="input-field"
                required
              >
                <option value="">Sélectionner un praticien</option>
                {practitioners.map((practitioner) => (
                  <option key={practitioner.id} value={practitioner.id}>
                    {practitioner.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Service *</label>
              <select
                value={formData.serviceId || ''}
                onChange={(e) => handleInputChange('serviceId', e.target.value)}
                className="input-field"
                required
              >
                <option value="">Sélectionner un service</option>
                {services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Client</label>
              <select
                value={formData.clientId || ''}
                onChange={(e) => handleInputChange('clientId', e.target.value || undefined)}
                className="input-field"
              >
                <option value="">Aucun client</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Rendez-vous</label>
              <select
                value={formData.appointmentId || ''}
                onChange={(e) => handleInputChange('appointmentId', e.target.value || undefined)}
                className="input-field"
              >
                <option value="">Aucun rendez-vous</option>
                {appointments.map((appointment) => (
                  <option key={appointment.id} value={appointment.id}>
                    {appointment.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Statut</label>
              <select
                value={formData.status || 'PLANNED'}
                onChange={(e) => handleInputChange('status', e.target.value as UpdateActData['status'])}
                className="input-field"
              >
                <option value="PLANNED">Planifié</option>
                <option value="IN_PROGRESS">En cours</option>
                <option value="COMPLETED">Terminé</option>
                <option value="CANCELLED">Annulé</option>
              </select>
            </div>

            <div className="md:col-span-2 lg:col-span-3">
              <label className="block text-sm font-medium text-textPrimary mb-2">Début planifié</label>
             <DateTimeInput
              value={plannedStartAtInput}
              onChange={setPlannedStartAtInput}
              className="input-field"
            />
            </div>

            <div className="md:col-span-2 lg:col-span-3">
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Fin planifiée
              </label>

              <DateTimeInput
                value={plannedEndAtInput}
                onChange={setPlannedEndAtInput}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Séquence</label>
              <input
                type="number"
                min={1}
                value={formData.sequence || ''}
                onChange={(e) => handleInputChange('sequence', e.target.value ? parseInt(e.target.value, 10) : undefined)}
                className="input-field"
                placeholder="1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Durée (minutes)</label>
              <input
                type="number"
                min={1}
                value={formData.durationMinutes || ''}
                onChange={(e) =>
                  handleInputChange('durationMinutes', e.target.value ? parseInt(e.target.value, 10) : undefined)
                }
                className="input-field"
                placeholder="30"
              />
            </div>

            <div className="md:col-span-2 lg:col-span-1">
              <label className="block text-sm font-medium text-textPrimary mb-2">Réalisé le</label>
              <DateTimeInput
                value={performedAtInput}
                onChange={setPerformedAtInput}
                className="input-field"
              />
            </div>

            <div className="md:col-span-2 lg:col-span-3">
              <label className="block text-sm font-medium text-textPrimary mb-2">Notes</label>
              <textarea
                value={formData.notes || ''}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                rows={4}
                className="input-field"
                placeholder="Notes internes sur l'acte..."
              />
            </div>
          </div>
        </div>

        {/* ── PIERCING RECORD ───────────────────────────────────────── */}
        {act?.serviceCategory === 'PIERCING' && (
          <div className="card border-l-4 border-l-violet-400">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <Stethoscope className="w-5 h-5 text-violet-600" />
                <h2 className="text-xl font-semibold text-textPrimary">Fiche piercing</h2>
                {piercingRecord && (
                  <span className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full">Enregistrée</span>
                )}
              </div>
              <button
                type="button"
                onClick={handlePiercingSubmit}
                disabled={savingPiercing}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 text-sm"
              >
                <Save className="w-4 h-4" />
                <span>{savingPiercing ? 'Enregistrement...' : 'Sauvegarder la fiche'}</span>
              </button>
            </div>

            {piercingError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-red-800 text-sm">{piercingError}</p>
              </div>
            )}

            {!act.clientId || !act.practitionerId ? (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                Un client et un praticien doivent être associés à l'acte avant de renseigner la fiche piercing.
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Type de piercing */}
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">Type de piercing *</label>
                  <select
                    value={piercingForm.piercingType}
                    onChange={(e) => handlePiercingChange('piercingType', e.target.value as PiercingType)}
                    className="input-field"
                  >
                    <option value="EAR_LOBE">Lobe de l'oreille</option>
                    <option value="EAR_CARTILAGE">Cartilage de l'oreille</option>
                    <option value="NOSE">Nez</option>
                    <option value="EYEBROW">Sourcil</option>
                    <option value="LIP">Lèvre</option>
                    <option value="TONGUE">Langue</option>
                    <option value="NAVEL">Nombril</option>
                    <option value="NIPPLE">Téton</option>
                    <option value="GENITAL">Génital</option>
                    <option value="SURFACE">Surface</option>
                    <option value="DERMAL">Dermal</option>
                  </select>
                </div>

                {/* Jauge aiguille */}
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">Jauge de l'aiguille *</label>
                  <input
                    type="text"
                    value={piercingForm.needleGauge}
                    onChange={(e) => handlePiercingChange('needleGauge', e.target.value)}
                    className="input-field"
                    placeholder="ex: 1.2mm (16G)"
                  />
                </div>

                {/* Temps de guérison */}
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">Temps de guérison estimé (semaines) *</label>
                  <input
                    type="number"
                    min={1}
                    value={piercingForm.healingTime}
                    onChange={(e) => handlePiercingChange('healingTime', parseInt(e.target.value, 10) || 1)}
                    className="input-field"
                  />
                </div>

                {/* Guéri */}
                <div className="flex items-center space-x-3 pt-6">
                  <input
                    type="checkbox"
                    id="isHealed"
                    checked={piercingForm.isHealed ?? false}
                    onChange={(e) => handlePiercingChange('isHealed', e.target.checked)}
                    className="w-4 h-4 text-violet-600 rounded"
                  />
                  <label htmlFor="isHealed" className="text-sm font-medium text-textPrimary">Piercing guéri</label>
                </div>

                {/* Placement */}
                <div className="md:col-span-2">
                  <p className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">Placement</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-textPrimary mb-1">Zone</label>
                      <input
                        type="text"
                        value={piercingForm.placement?.area ?? ''}
                        onChange={(e) => handlePiercingNested('placement', 'area', e.target.value)}
                        className="input-field"
                        placeholder="ex: Oreille, Nez..."
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-textPrimary mb-1">Côté</label>
                      <select
                        value={piercingForm.placement?.side ?? ''}
                        onChange={(e) => handlePiercingNested('placement', 'side', e.target.value || undefined)}
                        className="input-field"
                      >
                        <option value="">Non précisé</option>
                        <option value="left">Gauche</option>
                        <option value="right">Droit</option>
                        <option value="center">Centre</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-textPrimary mb-1">Précision</label>
                      <input
                        type="text"
                        value={piercingForm.placement?.specific ?? ''}
                        onChange={(e) => handlePiercingNested('placement', 'specific', e.target.value)}
                        className="input-field"
                        placeholder="ex: Hélix, Tragus..."
                      />
                    </div>
                  </div>
                </div>

                {/* Bijou */}
                <div className="md:col-span-2">
                  <p className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">Bijou posé</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-textPrimary mb-1">Type</label>
                      <input
                        type="text"
                        value={piercingForm.jewelry?.type ?? ''}
                        onChange={(e) => handlePiercingNested('jewelry', 'type', e.target.value)}
                        className="input-field"
                        placeholder="ex: Anneau, Barbell..."
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-textPrimary mb-1">Matière</label>
                      <input
                        type="text"
                        value={piercingForm.jewelry?.material ?? ''}
                        onChange={(e) => handlePiercingNested('jewelry', 'material', e.target.value)}
                        className="input-field"
                        placeholder="ex: Titane implant grade"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-textPrimary mb-1">Jauge</label>
                      <input
                        type="text"
                        value={piercingForm.jewelry?.gauge ?? ''}
                        onChange={(e) => handlePiercingNested('jewelry', 'gauge', e.target.value)}
                        className="input-field"
                        placeholder="ex: 1.2mm"
                      />
                    </div>
                  </div>
                </div>

                {/* Technique */}
                <div className="md:col-span-2">
                  <p className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">Technique</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-textPrimary mb-1">Méthode</label>
                      <input
                        type="text"
                        value={piercingForm.technique?.method ?? ''}
                        onChange={(e) => handlePiercingNested('technique', 'method', e.target.value)}
                        className="input-field"
                        placeholder="ex: Aiguille standard, Dermal punch..."
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-textPrimary mb-1">Outils utilisés</label>
                      <input
                        type="text"
                        value={piercingForm.technique?.tools ?? ''}
                        onChange={(e) => handlePiercingNested('technique', 'tools', e.target.value)}
                        className="input-field"
                        placeholder="ex: Pince, cathéter..."
                      />
                    </div>
                  </div>
                </div>

                {/* Aftercare */}
                <div className="md:col-span-2">
                  <p className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">Soins fournis</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-textPrimary mb-1">Produits remis</label>
                      <textarea
                        value={piercingForm.aftercareProvided?.products ?? ''}
                        onChange={(e) => handlePiercingNested('aftercareProvided', 'products', e.target.value)}
                        rows={2}
                        className="input-field"
                        placeholder="ex: Spray NeilMed, Notice de soin..."
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-textPrimary mb-1">Instructions données</label>
                      <textarea
                        value={piercingForm.aftercareProvided?.instructions ?? ''}
                        onChange={(e) => handlePiercingNested('aftercareProvided', 'instructions', e.target.value)}
                        rows={2}
                        className="input-field"
                        placeholder="ex: Nettoyer 2x/jour avec spray salin..."
                      />
                    </div>
                  </div>
                </div>

                {/* Complications */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-textPrimary mb-2">Complications observées</label>
                  <input
                    type="text"
                    value={piercingComplicationsText}
                    onChange={(e) => setPiercingComplicationsText(e.target.value)}
                    className="input-field"
                    placeholder="Séparer par des virgules : ex: Légère irritation, Gonflement..."
                  />
                  <p className="text-xs text-gray-400 mt-1">Laisser vide si aucune complication.</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── TATTOO RECORD ─────────────────────────────────────────── */}
        {act?.serviceCategory === 'TATTOO' && (
          <div className="card border-l-4 border-l-orange-400">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <Stethoscope className="w-5 h-5 text-orange-600" />
                <h2 className="text-xl font-semibold text-textPrimary">Fiche tatouage</h2>
                {tattooRecord && (
                  <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">Enregistrée</span>
                )}
              </div>
              <button
                type="button"
                onClick={handleTattooSubmit}
                disabled={savingTattoo}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 text-sm"
              >
                <Save className="w-4 h-4" />
                <span>{savingTattoo ? 'Enregistrement...' : 'Sauvegarder la fiche'}</span>
              </button>
            </div>

            {tattooError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-red-800 text-sm">{tattooError}</p>
              </div>
            )}

            {!act.clientId || !act.practitionerId ? (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                Un client et un praticien doivent être associés à l'acte avant de renseigner la fiche tatouage.
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Style */}
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">Style</label>
                  <select
                    value={tattooForm.style ?? ''}
                    onChange={(e) => handleTattooChange('style', (e.target.value as TattooStyle) || undefined)}
                    className="input-field"
                  >
                    <option value="">Non précisé</option>
                    <option value="TRADITIONAL">Traditionnel</option>
                    <option value="REALISM">Réalisme</option>
                    <option value="WATERCOLOR">Aquarelle</option>
                    <option value="GEOMETRIC">Géométrique</option>
                    <option value="MINIMALIST">Minimaliste</option>
                    <option value="BLACKWORK">Blackwork</option>
                    <option value="DOTWORK">Dotwork</option>
                    <option value="TRIBAL">Tribal</option>
                    <option value="JAPANESE">Japonais</option>
                    <option value="BIOMECHANICAL">Biomécanique</option>
                  </select>
                </div>

                {/* Color type */}
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">Type de couleur</label>
                  <select
                    value={tattooForm.colorType ?? ''}
                    onChange={(e) => handleTattooChange('colorType', (e.target.value as ColorType) || undefined)}
                    className="input-field"
                  >
                    <option value="">Non précisé</option>
                    <option value="BLACK_GREY">Noir & gris</option>
                    <option value="COLOR">Couleur</option>
                    <option value="MIXED">Mixte</option>
                  </select>
                </div>

                {/* Complexity */}
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">Complexité</label>
                  <select
                    value={tattooForm.complexity ?? ''}
                    onChange={(e) => handleTattooChange('complexity', (e.target.value as ComplexityLevel) || undefined)}
                    className="input-field"
                  >
                    <option value="">Non précisée</option>
                    <option value="SIMPLE">Simple</option>
                    <option value="MODERATE">Modérée</option>
                    <option value="COMPLEX">Complexe</option>
                    <option value="MASTERPIECE">Chef-d'œuvre</option>
                  </select>
                </div>

                {/* Completed */}
                <div className="flex items-center space-x-3 pt-6">
                  <input
                    type="checkbox"
                    id="tattooIsCompleted"
                    checked={tattooForm.isCompleted ?? false}
                    onChange={(e) => handleTattooChange('isCompleted', e.target.checked)}
                    className="w-4 h-4 text-orange-600 rounded"
                  />
                  <label htmlFor="tattooIsCompleted" className="text-sm font-medium text-textPrimary">Tatouage terminé</label>
                </div>

                {/* Size */}
                <div className="md:col-span-2">
                  <p className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">Dimensions</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-textPrimary mb-1">Largeur</label>
                      <input
                        type="number"
                        min={0}
                        step={0.1}
                        value={tattooForm.size?.width ?? ''}
                        onChange={(e) => handleTattooNested('size', 'width', e.target.value ? parseFloat(e.target.value) : undefined)}
                        className="input-field"
                        placeholder="ex: 10"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-textPrimary mb-1">Hauteur</label>
                      <input
                        type="number"
                        min={0}
                        step={0.1}
                        value={tattooForm.size?.height ?? ''}
                        onChange={(e) => handleTattooNested('size', 'height', e.target.value ? parseFloat(e.target.value) : undefined)}
                        className="input-field"
                        placeholder="ex: 8"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-textPrimary mb-1">Unité</label>
                      <select
                        value={tattooForm.size?.unit ?? 'cm'}
                        onChange={(e) => handleTattooNested('size', 'unit', e.target.value)}
                        className="input-field"
                      >
                        <option value="cm">cm</option>
                        <option value="inch">inch</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Placement */}
                <div className="md:col-span-2">
                  <p className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">Placement</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-textPrimary mb-1">Zone précise</label>
                      <input
                        type="text"
                        value={tattooForm.placement?.specific ?? ''}
                        onChange={(e) => handleTattooNested('placement', 'specific', e.target.value)}
                        className="input-field"
                        placeholder="ex: Avant-bras intérieur"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-textPrimary mb-1">Côté</label>
                      <select
                        value={tattooForm.placement?.side ?? ''}
                        onChange={(e) => handleTattooNested('placement', 'side', e.target.value)}
                        className="input-field"
                      >
                        <option value="">Non précisé</option>
                        <option value="left">Gauche</option>
                        <option value="right">Droit</option>
                        <option value="center">Centre</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Session notes */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-textPrimary mb-2">Notes de session</label>
                  <textarea
                    value={tattooForm.sessionNotes ?? ''}
                    onChange={(e) => handleTattooChange('sessionNotes', e.target.value)}
                    rows={3}
                    className="input-field"
                    placeholder="Observations, encres utilisées, durée effective..."
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── ACT PHOTOS ────────────────────────────────────────────── */}
        {act && (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-textPrimary">Photos de séance</h2>
              <button
                type="button"
                onClick={() => setPhotoCaptureOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
              >
                <Camera className="w-4 h-4" />
                Prendre une photo
              </button>
            </div>
            <ActPhotoGallery photos={actPhotos} onDelete={handlePhotoDelete} />
          </div>
        )}

        <div className="card">
          <h2 className="text-xl font-semibold text-textPrimary mb-4">Documents disponibles</h2>
          {!act?.documents || act.documents.length === 0 ? (
            <p className="text-sm text-gray-500">Aucun document généré pour cet acte.</p>
          ) : (
            <div className="space-y-2">
              {act.documents.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="text-sm text-gray-700">
                    <div className="font-medium">Document {doc.id.slice(0, 8)}</div>
                    <div className="text-gray-500">
                      Contrat: {doc.contractId.slice(0, 8)} · {new Date(doc.createdAt).toLocaleString('fr-FR')}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {doc.hasPdf && (
                      <button
                        type="button"
                        onClick={() => openGeneratedDocument(doc.id, 'pdf')}
                        className="px-3 py-1.5 text-sm text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-md inline-flex items-center gap-1"
                      >
                        <Eye className="w-4 h-4" />
                        Voir PDF
                      </button>
                    )}
                    {doc.hasDocx && (
                      <button
                        type="button"
                        onClick={() => openGeneratedDocument(doc.id, 'docx')}
                        className="px-3 py-1.5 text-sm text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-md inline-flex items-center gap-1"
                      >
                        <Eye className="w-4 h-4" />
                        Voir DOCX
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </form>

      <ActPhotoCaptureModal
        isOpen={photoCaptureOpen}
        onClose={() => setPhotoCaptureOpen(false)}
        onSave={handlePhotoCapture}
      />
    </div>
  );
}
